import React from 'react';
import { User } from '../../../types';
import MobileUserCard from './MobileUserCard';
import UserFilterBar from './UserFilterBar';
import UserPagination from './UserPagination';
import { useAdminTranslation } from '../../../modules/i18n';
import { useKeycloakAuth } from '../../../contexts/KeycloakAuthProvider';
import { jwtDecode } from 'jwt-decode';
import { isActive } from './user-status-utils';

export interface UsersContentProps {
  users: User[];
  loading: boolean;
  onEditUser: (user: User) => void;
  onDeleteUser: (userId: string) => void;
  onDisableUser: (userId: string) => void;
  onEnableUser: (userId: string) => void;
  onViewUserDetail: (user: User) => void;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalCount: number;
  showingFrom: number;
  showingTo: number;
  onPageChange: (page: number) => void;

  // Filters
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

const UsersContent: React.FC<UsersContentProps> = ({
  users,
  loading,
  onEditUser,
  onDeleteUser,
  onDisableUser,
  onEnableUser,
  onViewUserDetail,
  currentPage,
  totalPages,
  totalCount,
  showingFrom,
  showingTo,
  onPageChange,
  searchQuery,
  onSearchChange
}) => {
  const { t } = useAdminTranslation();
  const { getAccessToken } = useKeycloakAuth();

  const getCurrentUserEmail = () => {
    try {
      const token = getAccessToken();
      if (token) {
        const decoded: any = jwtDecode(token);
        return decoded.email || decoded.preferred_username || '';
      }
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
    return '';
  };

  const currentUserEmail = getCurrentUserEmail();

  return (
    <div className='flex h-full flex-col gap-4'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h2 className='text-lg font-medium text-gray-900 dark:text-dark-text'>{t('content.users.title')}</h2>
          <p className='mt-1 text-sm text-gray-500 dark:text-dark-text-secondary'>{t('tabs.users.description')}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <UserFilterBar searchQuery={searchQuery} onSearchChange={onSearchChange} />

      {/* Table Content */}
      <div className='min-h-0 flex-1 overflow-hidden'>
        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent' />
          </div>
        ) : users.length === 0 ? (
          <div className='py-12 text-center text-sm text-gray-500 dark:text-dark-text-secondary'>
            {t('content.users.noResults')}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className='hidden min-h-0 overflow-hidden rounded-lg border border-gray-200 dark:border-border-dark lg:block'>
              <div className='scrollbar-thin max-h-full overflow-auto'>
                <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                  <thead className='sticky top-0 z-10 bg-gray-50 shadow-sm dark:bg-dark-background-tertiary'>
                    <tr>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.users.tableHeaders.user')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.users.tableHeaders.role')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.users.tableHeaders.status')}
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
                        {t('content.users.tableHeaders.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-dark-background-secondary'>
                    {users.map((user) => (
                      <tr key={user.id} className='transition-colors hover:bg-gray-50 dark:hover:bg-dark-background-hover'>
                        <td className='whitespace-nowrap px-6 py-4'>
                          <div>
                            <div className='text-sm font-medium text-gray-900 dark:text-dark-text'>{user.username}</div>
                            <div className='text-sm text-gray-500 dark:text-dark-text-secondary'>{user.email}</div>
                          </div>
                        </td>
                        <td className='whitespace-nowrap px-6 py-4'>
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                              user.role?.toUpperCase() === 'ADMIN'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                : user.role?.toUpperCase() === 'MODERATOR'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'bg-gray-100 text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text'
                            }`}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td className='whitespace-nowrap px-6 py-4'>
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                              isActive(user.status)
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}
                          >
                            {isActive(user.status)
                              ? t('content.users.status.active')
                              : t('content.users.status.inactive')}
                          </span>
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-right text-sm font-medium'>
                          <button
                            onClick={() => onEditUser(user)}
                            className='mr-3 rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800 dark:text-blue-400 dark:hover:bg-sky-400/50 dark:hover:text-blue-300'
                            title={t('content.users.actions.edit')}
                          >
                            <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                              />
                            </svg>
                          </button>
                          {user.email !== currentUserEmail && isActive(user.status) && (
                            <button
                              onClick={() => onDisableUser(user.id)}
                              className='mr-2 rounded p-1 text-orange-600 transition-colors hover:bg-orange-50 hover:text-orange-800 dark:text-orange-400 dark:hover:bg-orange-400/30'
                              title={t('content.users.actions.disable')}
                            >
                              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' />
                              </svg>
                            </button>
                          )}
                          {user.email !== currentUserEmail && !isActive(user.status) && (
                            <button
                              onClick={() => onEnableUser(user.id)}
                              className='mr-2 rounded p-1 text-green-600 transition-colors hover:bg-green-50 hover:text-green-800 dark:text-green-400 dark:hover:bg-green-400/30'
                              title={t('content.users.actions.enable')}
                            >
                              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className='h-full space-y-4 overflow-y-auto lg:hidden'>
              {users.map((user) => (
                <MobileUserCard
                  key={user.id}
                  user={user}
                  onEdit={() => onEditUser(user)}
                  onDelete={() => onDeleteUser(user.id)}
                  onDisable={() => onDisableUser(user.id)}
                  onEnable={() => onEnableUser(user.id)}
                  onViewDetail={() => onViewUserDetail(user)}
                  isCurrentUser={user.email === currentUserEmail}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      <UserPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        showingFrom={showingFrom}
        showingTo={showingTo}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default UsersContent;
